import React, { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import RangeTrack from '@/components/market/RangeTrack'
import { formatMoney } from '@/lib/format'
import { DEMAND_STYLE, TONE_STYLE } from '@/lib/market'
import { cn } from '@/lib/utils'
import type { MarketNight, MarketPriceDomain, MarketTone } from '@/types/market'

const INITIAL_ROWS = 7

/**
 * The phone's path through the nights: one 48px row per night (date, where you sit on the price
 * ruler, your price, market demand). A tap opens that night in the bottom sheet.
 */
export default function NightRangeList({
  nights,
  attention,
  currency,
  domain,
  selectedDate,
  onOpen,
}: {
  nights: MarketNight[]
  attention: Map<string, MarketTone>
  currency: string
  domain: MarketPriceDomain
  selectedDate: string | null
  onOpen: (date: string, trigger: HTMLButtonElement) => void
}) {
  const [showAll, setShowAll] = useState(false)
  const shown = showAll ? nights : nights.slice(0, INITIAL_ROWS)

  return (
    <div>
      <ul className="divide-y divide-border/40 overflow-hidden rounded-lg border border-border/60" aria-label="Nights">
        {shown.map((night) => {
          const tone = attention.get(night.date)
          return (
            <li key={night.date}>
              <button
                type="button"
                onClick={(e) => onOpen(night.date, e.currentTarget)}
                aria-label={`${night.label}: ${night.verdict}`}
                className={cn(
                  'relative flex h-12 w-full cursor-pointer items-center gap-3 pl-3 pr-2 text-left transition-colors hover:bg-muted/40',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40',
                  night.isWeekend && 'bg-slate-50',
                  night.date === selectedDate && 'bg-primary/5'
                )}
              >
                {tone && (
                  <span
                    aria-hidden="true"
                    className={cn('absolute inset-y-0 left-0 w-[3px]', TONE_STYLE[tone].accent)}
                  />
                )}
                <span className="flex w-9 shrink-0 flex-col items-center leading-none">
                  <span className="text-[10px] font-medium uppercase text-muted-foreground">{night.weekday}</span>
                  <span className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">{night.dayOfMonth}</span>
                </span>
                <RangeTrack
                  min={night.compsetMin}
                  median={night.compsetMedian}
                  max={night.compsetMax}
                  price={night.yourPrice}
                  domain={domain}
                  className="min-w-0 flex-1"
                />
                <span className="w-14 shrink-0 text-right text-sm font-semibold tabular-nums text-primary">
                  {night.yourPrice == null ? (
                    <span className="text-xs font-normal text-muted-foreground">--</span>
                  ) : (
                    formatMoney(night.yourPrice, currency)
                  )}
                </span>
                <span
                  aria-hidden="true"
                  className={cn(
                    'h-2 w-2 shrink-0 rounded-full',
                    night.marketDemand === 'unknown' ? 'bg-slate-100' : DEMAND_STYLE[night.marketDemand].dot
                  )}
                />
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
              </button>
            </li>
          )
        })}
      </ul>
      {nights.length > INITIAL_ROWS && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-2 inline-flex h-9 w-full cursor-pointer items-center justify-center gap-1 rounded-lg text-xs font-medium text-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          {showAll ? 'Show fewer nights' : `Show all ${nights.length} nights`}
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showAll && 'rotate-180')} />
        </button>
      )}
    </div>
  )
}
